import classnames from 'classnames';
import * as React from 'react';
import { callAll } from '../helper/fp';

export type Option = {
  label: string;
  value: string;
};

const normalizeOption = (option: Option | string): Option =>
  typeof option === 'string'
    ? {
        label: option,
        value: option,
      }
    : option;

export type SelectProps = {
  /**
   * callback that will be invoked with the new value when value is changed.
   */
  onChangeValue?: (value: string) => void;
  /**
   * options that will be rendered as children, you can also construct your option markup as children
   */
  options?: Array<Option | string>;
} & JSX.IntrinsicElements['select'];

/**
 * Select is a wrapper around `<select>` html element,
 * thus it would accepts all props a `<select />` accepts.
 * You can configure the options by passing down `options` props
 * or construct the `<option>` markup like a `select` element.
 * Ref will be forwarded to underlying `<select>` element
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function SelectComp(
  { className, onChangeValue, onChange, children, options, ...props },
  ref
) {
  return (
    <select
      className={classnames('form-control', className)}
      onChange={callAll(onChange, onChangeValue && ((ev) => onChangeValue(ev.target.value)))}
      {...props}
      ref={ref}
    >
      {options
        ? options.map(normalizeOption).map((opt, i) => (
            <option value={opt.value} aria-selected={opt.value === props.value} key={i}>
              {opt.label}
            </option>
          ))
        : children}
    </select>
  );
});
